import React, { useEffect, useState } from 'react';
import {
    Box, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Alert, useTheme, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Tooltip, MenuItem
} from '@mui/material';
import Layout from './Layout';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';

const Clienti = () => {
    const [clienti, setClienti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [cities, setCities] = useState([]);

    const [clientForm, setClientForm] = useState({
        ragionesociale: '',
        nomecliente: '',
        viacliente: '',
        cap: '',
        piva: '',
        codice_fiscale: '',
        viafatturazione: '',
        capfatturazione: '',
        idcomune_fk: '',
        idcomunefatturazione_fk: '',
        emailfatturazione: '',
        PECDestinatario: '',
        CodiceDestinatario: '',
        telefono1: ''
    });

    const theme = useTheme();

    const fetchClienti = () => {
        const token = localStorage.getItem('token');
        fetch('/papi2/clients', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then(response => {
                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        localStorage.removeItem('token');
                        window.location.href = '/login';
                    }
                    throw new Error('Errore nel caricamento dei clienti');
                }
                return response.json();
            })
            .then(data => {
                setClienti(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    };

    const fetchCities = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/papi2/masterdata/getCities', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCities(data);
            }
        } catch (err) {
            console.error("Error fetching cities:", err);
        }
    };

    useEffect(() => {
        fetchClienti();
        fetchCities();
    }, []);

    const handleOpenEditModal = (cliente) => {
        setSelectedCliente(cliente);
        setClientForm({
            ragionesociale: cliente.ragionesociale || '',
            nomecliente: cliente.nomecliente || '',
            viacliente: cliente.viacliente || '',
            cap: cliente.cap || '',
            piva: cliente.piva || '',
            codice_fiscale: cliente.codice_fiscale || '',
            viafatturazione: cliente.viafatturazione || '',
            capfatturazione: cliente.capfatturazione || '',
            idcomune_fk: cliente.idcomune_fk || '',
            idcomunefatturazione_fk: cliente.idcomunefatturazione_fk || '',
            emailfatturazione: cliente.emailfatturazione || '',
            PECDestinatario: cliente.PECDestinatario || '',
            CodiceDestinatario: cliente.CodiceDestinatario || '',
            telefono1: cliente.telefono1 || ''
        });
        setOpenEditModal(true);
    };

    const handleOpenAddModal = () => {
        setClientForm({
            ragionesociale: '',
            nomecliente: '',
            viacliente: '',
            cap: '',
            piva: '',
            codice_fiscale: '',
            viafatturazione: '',
            capfatturazione: '',
            idcomune_fk: '',
            idcomunefatturazione_fk: '',
            emailfatturazione: '',
            PECDestinatario: '',
            CodiceDestinatario: '',
            telefono1: ''
        });
        setOpenAddModal(true);
    };

    const handleSave = (isEdit) => {
        const token = localStorage.getItem('token');
        const url = isEdit ? `/papi2/clients/${selectedCliente.idcliente}` : '/papi2/clients';
        const method = isEdit ? 'PUT' : 'POST';

        fetch(url, {
            method: method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(clientForm),
        })
            .then(response => {
                if (!response.ok) throw new Error('Errore durante il salvataggio');
                return response.json();
            })
            .then(() => {
                setOpenEditModal(false);
                setOpenAddModal(false);
                fetchClienti();
            })
            .catch(err => {
                alert(err.message);
            });
    };

    const handleDelete = (idcliente) => {
        if (!window.confirm("Sei sicuro di voler eliminare questo cliente?")) return;
        const token = localStorage.getItem('token');
        fetch(`/papi2/clients/${idcliente}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(response => {
                if (!response.ok) {
                    return response.text().then(text => { throw new Error(text || 'Errore durante l\'eliminazione'); });
                }
                fetchClienti();
            })
            .catch(err => {
                alert(err.message);
            });
    };

    return (
        <Layout>
            <Box sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                        Anagrafica Clienti
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddModal}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        Nuovo Cliente
                    </Button>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <TableContainer component={Paper} sx={{ bgcolor: theme.palette.background.paper, boxShadow: 3, borderRadius: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Ragione Sociale</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>P.IVA / CF</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Città</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Email Fatt.</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Progetti / Contratti</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Azioni</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clienti.map((cliente) => (
                                    <TableRow key={cliente.idcliente} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{cliente.ragionesociale}</TableCell>
                                        <TableCell>{cliente.nomecliente}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" display="block">P.IVA: {cliente.piva}</Typography>
                                            <Typography variant="caption" display="block">CF: {cliente.codice_fiscale}</Typography>
                                        </TableCell>
                                        <TableCell>{cliente.comune_residenza}</TableCell>
                                        <TableCell>{cliente.emailfatturazione}</TableCell>
                                        <TableCell align="center">
                                            {cliente.num_progetti} / {cliente.num_contratti}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" justifyContent="center" gap={1}>
                                                <Tooltip title="Modifica">
                                                    <IconButton onClick={() => handleOpenEditModal(cliente)} color="primary" size="small">
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {(cliente.num_progetti === 0 && cliente.num_contratti === 0) && (
                                                    <Tooltip title="Elimina">
                                                        <IconButton onClick={() => handleDelete(cliente.idcliente)} color="error" size="small">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Form Modal (Add & Edit) */}
                <Dialog open={openAddModal || openEditModal} onClose={() => { setOpenAddModal(false); setOpenEditModal(false); }} maxWidth="md" fullWidth>
                    <DialogTitle>{openAddModal ? 'Nuovo Cliente' : 'Modifica Cliente'}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                                label="Ragione Sociale"
                                fullWidth
                                value={clientForm.ragionesociale}
                                onChange={(e) => setClientForm({ ...clientForm, ragionesociale: e.target.value })}
                            />
                            <TextField
                                label="Nome Cliente (Breve)"
                                fullWidth
                                value={clientForm.nomecliente}
                                onChange={(e) => setClientForm({ ...clientForm, nomecliente: e.target.value })}
                            />
                            <TextField
                                label="P.IVA"
                                fullWidth
                                value={clientForm.piva}
                                onChange={(e) => setClientForm({ ...clientForm, piva: e.target.value })}
                            />
                            <TextField
                                label="Codice Fiscale"
                                fullWidth
                                value={clientForm.codice_fiscale}
                                onChange={(e) => setClientForm({ ...clientForm, codice_fiscale: e.target.value })}
                            />
                            <TextField
                                label="Indirizzo"
                                fullWidth
                                value={clientForm.viacliente}
                                onChange={(e) => setClientForm({ ...clientForm, viacliente: e.target.value })}
                            />
                            <TextField
                                label="CAP"
                                fullWidth
                                value={clientForm.cap}
                                onChange={(e) => setClientForm({ ...clientForm, cap: e.target.value })}
                            />
                            <TextField
                                select
                                label="Comune"
                                fullWidth
                                value={clientForm.idcomune_fk}
                                onChange={(e) => setClientForm({ ...clientForm, idcomune_fk: e.target.value })}
                            >
                                {cities.map((city) => (
                                    <MenuItem key={city.idcomune} value={city.idcomune}>
                                        {city.nomecomune}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Telefono"
                                fullWidth
                                value={clientForm.telefono1}
                                onChange={(e) => setClientForm({ ...clientForm, telefono1: e.target.value })}
                            />
                            <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
                                <Typography variant="h6" gutterBottom>Dati Fatturazione</Typography>
                            </Box>
                            <TextField
                                label="Indirizzo Fatturazione"
                                fullWidth
                                value={clientForm.viafatturazione}
                                onChange={(e) => setClientForm({ ...clientForm, viafatturazione: e.target.value })}
                            />
                            <TextField
                                label="CAP Fatturazione"
                                fullWidth
                                value={clientForm.capfatturazione}
                                onChange={(e) => setClientForm({ ...clientForm, capfatturazione: e.target.value })}
                            />
                            <TextField
                                select
                                label="Comune Fatturazione"
                                fullWidth
                                value={clientForm.idcomunefatturazione_fk}
                                onChange={(e) => setClientForm({ ...clientForm, idcomunefatturazione_fk: e.target.value })}
                            >
                                {cities.map((city) => (
                                    <MenuItem key={city.idcomune} value={city.idcomune}>
                                        {city.nomecomune}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Email Fatturazione"
                                fullWidth
                                value={clientForm.emailfatturazione}
                                onChange={(e) => setClientForm({ ...clientForm, emailfatturazione: e.target.value })}
                            />
                            <TextField
                                label="PEC / Codice Destinatario (SDI)"
                                fullWidth
                                value={clientForm.PECDestinatario}
                                onChange={(e) => setClientForm({ ...clientForm, PECDestinatario: e.target.value })}
                            />
                            <TextField
                                label="Codice Destinatario"
                                fullWidth
                                value={clientForm.CodiceDestinatario}
                                onChange={(e) => setClientForm({ ...clientForm, CodiceDestinatario: e.target.value })}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { setOpenAddModal(false); setOpenEditModal(false); }}>Annulla</Button>
                        <Button onClick={() => handleSave(openEditModal)} variant="contained" color="primary">
                            Salva
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
};

export default Clienti;
